// Multi-tag classification for the community course library (Feb 2026).
// Courses can carry more than one tag — filter pills match ANY tag.

export const CATEGORIES = [
  "Clinical workflows",
  "Front-of-house & communication",
  "Practice management",
  "Information & digital governance",
  "Safeguarding & vulnerable groups",
  "Equality, diversity & inclusion",
  "Wellbeing & workplace safety",
];

// Each course lists every tag it belongs to (primary tag first for consistency).
const TAGS_BY_SLUG = {
  // Clinical workflows (core)
  "basic-life-support-gp-practice": ["Clinical workflows"],
  "chaperoning-gp-practice": ["Clinical workflows", "Safeguarding & vulnerable groups"],
  "consent-practice-teams": ["Clinical workflows"],
  "controlled-drugs-gp-practice": ["Clinical workflows"],
  "death-administration-gp-practice": ["Clinical workflows", "Practice management"],
  "fit-notes-med3-general-practice": ["Clinical workflows", "Practice management"],
  "usc-pathway-cancer-referrals": ["Clinical workflows", "Practice management"],
  "cervical-screening-sccrs": ["Clinical workflows"],
  "repeat-prescribing-sop": ["Clinical workflows", "Practice management"],
  "vaccine-cold-chain": ["Clinical workflows"],
  "home-visits-sop": ["Clinical workflows"],
  "frailty-general-practice": ["Clinical workflows"],
  "deprescribing-fundamentals": ["Clinical workflows"],
  "stopp-start-v3": ["Clinical workflows"],
  "anticoagulation-general-practice": ["Clinical workflows"],
  "diabetes-annual-review": ["Clinical workflows"],
  "end-of-life-prescribing-acp": ["Clinical workflows"],

  // Front-of-house & communication
  "call-handling-signposting": ["Front-of-house & communication"],
  "de-escalation-difficult-conversations": ["Front-of-house & communication", "Wellbeing & workplace safety"],
  "civility-saves-lives": ["Front-of-house & communication", "Wellbeing & workplace safety"],
  "motivational-interviewing-basics": ["Front-of-house & communication", "Clinical workflows"],
  "complaints-management-practice-teams": ["Front-of-house & communication", "Practice management"],
  "medical-terminology-uk": ["Front-of-house & communication", "Clinical workflows"],

  // Practice management
  "enhanced-services-payment-cycles": ["Practice management"],
  "new-patient-registration-scotland": ["Practice management", "Front-of-house & communication"],
  "cql-cluster-meetings": ["Practice management"],
  "practice-finance-non-accountants": ["Practice management"],
  "pvg-scheme-scotland": ["Practice management", "Safeguarding & vulnerable groups"],
  "referrals-sci-gateway": ["Practice management", "Clinical workflows"],
  "whistleblowing-nhs-practice": ["Practice management", "Safeguarding & vulnerable groups"],
  "duty-of-candour-scotland": ["Practice management", "Clinical workflows"],
  "navigating-scottish-primary-care-structures": ["Practice management"],
  "pharmacology-uk": ["Practice management", "Clinical workflows"],

  // Information & digital governance
  "foisa-scotland": ["Information & digital governance", "Practice management"],
  "data-use-access-act-2025": ["Information & digital governance"],
  "gdpr-for-gp-surgeries": ["Information & digital governance"],
  "copilot-safe-use-nhsggc": ["Information & digital governance"],

  // Safeguarding & vulnerable groups
  "safeguarding-practice-teams": ["Safeguarding & vulnerable groups", "Clinical workflows"],
  "domestic-abuse-practice-teams": ["Safeguarding & vulnerable groups", "Front-of-house & communication"],
  "mental-health-suicide-awareness": ["Safeguarding & vulnerable groups", "Clinical workflows"],
  "gambling-harms-general-practice": ["Safeguarding & vulnerable groups", "Clinical workflows"],
  "working-with-interpreters": ["Safeguarding & vulnerable groups", "Front-of-house & communication", "Clinical workflows"],
  "homeless-friendly-practice": ["Safeguarding & vulnerable groups", "Clinical workflows"],
  "trauma-informed-care": ["Safeguarding & vulnerable groups", "Clinical workflows"],
  "working-in-deprivation-deep-end": ["Safeguarding & vulnerable groups", "Clinical workflows"],

  // Equality, diversity & inclusion
  "equality-diversity-inclusion-foundation": ["Equality, diversity & inclusion"],
  "equality-diversity-inclusion-intermediate": ["Equality, diversity & inclusion"],
  "equality-diversity-inclusion-advanced": ["Equality, diversity & inclusion", "Practice management"],
  "lgbtq-friendly-practice": ["Equality, diversity & inclusion"],
  "neurodiversity-friendly-practice": ["Equality, diversity & inclusion"],
  "veteran-friendly-practice": ["Equality, diversity & inclusion"],
  "emergency-services-friendly-practice": ["Equality, diversity & inclusion"],

  // Wellbeing & workplace safety
  "fire-safety-gp-surgery": ["Wellbeing & workplace safety"],
  "coshh-gp-practice": ["Wellbeing & workplace safety"],
  "manual-handling-gp-practice": ["Wellbeing & workplace safety"],
  "infection-control-gp-practice": ["Wellbeing & workplace safety", "Clinical workflows"],
  "bullying-in-the-practice": ["Wellbeing & workplace safety"],
  "managing-stress-and-workload": ["Wellbeing & workplace safety", "Clinical workflows"],
  "pm-stress-and-wellbeing": ["Wellbeing & workplace safety", "Practice management"],
  "menopause-in-the-workplace": ["Wellbeing & workplace safety", "Practice management"],
  "neurodiverse-colleagues-flourishing": ["Wellbeing & workplace safety", "Equality, diversity & inclusion", "Practice management"],
  "second-victim-living-through-a-claim": ["Wellbeing & workplace safety", "Clinical workflows"],
};

// Return the array of tags for a course.
// Prefers server-persisted `course.tags` (edited by superadmin / practice admin);
// falls back to the hardcoded TAGS_BY_SLUG map, then a heuristic on title/slug.
export function tagsFor(course) {
  if (!course) return [];
  if (Array.isArray(course.tags) && course.tags.length) return course.tags;
  const explicit = TAGS_BY_SLUG[course.slug];
  if (explicit && explicit.length) return explicit;
  // Fallback heuristic for any unmapped new course.
  const hay = `${course.slug || ""} ${course.title || ""} ${course.subtitle || ""}`.toLowerCase();
  const tags = [];
  if (/(safeguard|abuse|violence|protection|interpret|homeless|trauma|deprivation|deep end|mental health|suicide|gambling|pvg)/.test(hay)) tags.push("Safeguarding & vulnerable groups");
  if (/(life support|bls|resus|controlled drug|death|mccd|fit note|cervical|screening|repeat prescrib|cold chain|home visit|frail|consent|chaperon|deprescrib|stopp|anticoagul|diabetes|end.of.life|palliative)/.test(hay)) tags.push("Clinical workflows");
  if (/(call handl|reception|signpost|de-escalat|civility|motivational|communication|terminolog)/.test(hay)) tags.push("Front-of-house & communication");
  if (/(gdpr|foisa|data protection|data use|access act|copilot|information governance|digital)/.test(hay)) tags.push("Information & digital governance");
  if (/(neuro|lgbtq|veteran|emergency service|blue light|inclusi|equity|diversity)/.test(hay)) tags.push("Equality, diversity & inclusion");
  if (/(fire|coshh|manual|infection|bullying|stress|wellbeing|menopause|second.victim|burnout)/.test(hay)) tags.push("Wellbeing & workplace safety");
  if (/(enhanced service|payment|registration|cluster|cql|finance|referral|whistleblow|duty of candour|scottish primary care|complaint|pharma)/.test(hay)) tags.push("Practice management");
  return tags.length ? tags : ["Practice management"];
}

// Backwards-compat single-string helper (some legacy callers may still use it).
export function categoryFor(course) {
  const t = tagsFor(course);
  return t[0] || "Practice management";
}

export const LEVELS = ["Foundation", "Intermediate", "Advanced"];
